import React, { useEffect, useMemo, useState } from "react"
import { container } from "../../infrastructure/container"
import { SessionService } from "../../infrastructure/services/session"
import { SupportUserService } from "../../infrastructure/services/api/supportUser"
import { SupportRole } from "../../infrastructure/models/common/supportrole"
import defaultAvatar from "../../asset/profile_icon.png"
import { Box } from "@mui/material"
import { COLORS } from "../../presentation/constants/colors"
import { FranchiseService } from "../../infrastructure/services/api/franchise"
import UpdateSupportUserForm from "./UpdateSupportUserForm"
import { Dialog, DialogContent, Typography, } from "@mui/material"

const MyAccount: React.FC = () => {
  const sessionService = useMemo(() => container.resolve<SessionService>("sessionService"), [])
  const supportUserService = useMemo(() => container.resolve<SupportUserService>("supportUserService"), [])
  const franchiseService = useMemo(() => container.resolve<FranchiseService>("franchiseService"), [])

  const [currentUser, setCurrentUser] = useState(sessionService.user)
  const [userDetails, setUserDetails] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [oldPassword, setOldPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [passwordError, setPasswordError] = useState("")
  const [isUpdating, setIsUpdating] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [updateSuccessOpen, setUpdateSuccessOpen] = useState(false)
  const [passwordSuccessOpen, setPasswordSuccessOpen] = useState(false)

  const getRoleName = (role: number): string => SupportRole[role] || "Unknown"

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const users = await supportUserService.getSupportUsers()
        const foundUser = users.find((u: any) => u.email === currentUser?.email)
        if (foundUser) setUserDetails(foundUser)
        else setError("User not found in support user list.")
      } catch (err) {
        console.error("Failed to fetch user details:", err)
        setError("Unable to load account details.")
      } finally {
        setLoading(false)
      }
    }

    fetchUserDetails()
  }, [supportUserService, currentUser?.email])

  useEffect(() => {
    if (updateSuccessOpen) {
      const timer = setTimeout(() => setUpdateSuccessOpen(false), 2000)
      return () => clearTimeout(timer)
    }
  }, [updateSuccessOpen])

  useEffect(() => {
    if (passwordSuccessOpen) {
      const timer = setTimeout(() => setPasswordSuccessOpen(false), 2000)
      return () => clearTimeout(timer)
    }
  }, [passwordSuccessOpen])

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentUser({ ...sessionService.user })
    }, 1000)
    return () => clearInterval(interval)
  }, [sessionService])


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-gray-600">
        Loading your profile...
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-red-600">
        {error}
      </div>
    )
  }

  if (!userDetails) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-gray-600">
        No profile data available.
      </div>
    )
  }

  const address =
    currentUser && typeof currentUser.address === "object"
      ? `${(currentUser.address as any)?.street ?? ""}, ${(currentUser.address as any)?.city ?? ""}, ${(currentUser.address as any)?.state ?? ""} - ${(currentUser.address as any)?.postalCode ?? ""}`
      : currentUser?.address || "No address available"

  const handlePasswordChange = async () => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,24}$/

    // Check if all fields are filled
    if (!oldPassword || !newPassword || !confirmPassword) {
      setPasswordError("All fields are required.")
      return
    }

    // Validate new password
    if (!passwordRegex.test(newPassword)) {
      setPasswordError(
        "Password must be 8-24 characters long, include uppercase, lowercase, number, and special character."
      )
      return
    }

    // Check password match
    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match.")
      return
    }

    // Checks whether old password and new password is same or not
    if (oldPassword === newPassword) {
      setPasswordError("New password must be different from current password.");
      return;
    }

    try {
      setIsUpdating(true)
      setPasswordError("")

      await franchiseService.changePassword(oldPassword, newPassword)

      setPasswordSuccessOpen(true)
      setShowPasswordModal(false)
      setOldPassword("")
      setNewPassword("")
      setConfirmPassword("")
    } catch (err: any) {
      console.error("Password update failed:", err)
      setPasswordError(
        err?.response?.data?.message || "Failed to update password."
      )
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <div className="flex flex-col items-center p-6 bg-white rounded-xl shadow-md max-w-6xl mx-auto mt-10  overflow-y-auto max-h-[80vh] scrollbar-hide">
      {/* Profile Header */}
      <div className="flex flex-col sm:flex-row items-center justify-between w-full gap-6 sm:gap-10 border-b pb-6">
        {/* Left Section: Profile Image + Name */}
        <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8">
          <img
            src={currentUser.profileImage || defaultAvatar}
            alt="Profile"
            className="w-16 h-16 rounded-full object-cover shadow-md"
          />
          <div className="text-center sm:text-left">
            <h2 className="text-2xl font-bold text-gray-900">
              {currentUser.firstname} {currentUser.lastname}
            </h2>
          </div>
        </div>

        {/* Right Section: Edit Button */}
        {[SupportRole.SUPERADMIN, SupportRole.ADMIN].includes(currentUser.role) && (
          <button
            onClick={() => setIsEditing(true)}
            className="mt-4 sm:mt-0 px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium 
         hover:bg-blue-700 transition-colors duration-200 shadow-sm"
          >
            Edit
          </button>
        )}
      </div>

      {/* Personal Info */}
      <div className="w-full mt-6 space-y-4">
        <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">
          Personal Information
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm text-gray-700">
          {/* Email */}
          <div className="flex items-center justify-between sm:justify-start sm:gap-2">
            <strong className="w-24 text-gray-800">Email:</strong>
            <span>{currentUser.email}</span>
          </div>

          {/* Phone */}
          <div className="flex items-center justify-between sm:justify-start sm:gap-2">
            <strong className="w-24 text-gray-800">Phone:</strong>
            <span>{currentUser.phone || "Not available"}</span>
          </div>

          {/* Address */}
          <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-start sm:gap-2">
            <strong className="w-24 text-gray-800">Address:</strong>
            <span className="break-words">{address}</span>
          </div>

          {/* Role */}
          <div className="flex items-center justify-between sm:justify-start sm:gap-2">
            <strong className="w-24 text-gray-800">Role:</strong>
            <span className="inline-block bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
              {getRoleName(currentUser.role)}
            </span>
          </div>

          {/* Status */}
          <div className="flex items-center justify-between sm:justify-start sm:gap-2">
            <strong className="w-24 text-gray-800">Status:</strong>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                className="px-3 py-1 text-xs font-medium rounded-full text-white"
                sx={{
                  backgroundColor: currentUser.isActive
                    ? COLORS.ACTIVE
                    : COLORS.INACTIVE,
                }}
              >
                {currentUser.isActive ? "Active" : "Inactive"}
              </Box>
            </Box>
          </div>
        </div>

      </div>

      {/* Franchise Info */}
      <div className="w-full mt-8">
        <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">
          Allocated Franchises
        </h3>

        {userDetails.franchises && userDetails.franchises.length > 0 ? (
          <div className="flex flex-wrap gap-3 mt-3">
            {userDetails.franchises.map((f: any) => (
              <div
                key={f.franchiseId}
                className="w-[150px] h-[40px] flex items-center justify-center 
                   bg-gray-50 text-gray-900 border rounded-lg shadow-sm 
                   hover:shadow-md transition-shadow duration-200"
              >
                <span className="text-sm font-medium text-center leading-[1]">
                  {f.name}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-600 mt-3">No franchises allocated.</p>
        )}

      </div>

      {isEditing && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full overflow-y-auto max-h-[90vh]">
            <UpdateSupportUserForm
              defaultValues={{
                email: userDetails.email,
                firstname: userDetails.firstname || currentUser.firstname,
                lastname: userDetails.lastname || currentUser.lastname,
                role: userDetails.role,
                franchises: userDetails.franchises || [],
                isActive: userDetails.isActive,
                address: userDetails.address || {
                  street: "",
                  city: "",
                  state: "",
                  postalCode: "",
                },
              }}
              isLoading={isUpdating}
              onCancel={() => setIsEditing(false)}
              onSubmit={async (data) => {
                try {
                  setIsUpdating(true)
                  // Match your actual service signature
                  await supportUserService.updateSupportUser({
                    ...data,
                    email: userDetails.email,
                  })

                  // Refetch the updated user details immediately
                  const users = await supportUserService.getSupportUsers()
                  const updated = users.find((u: any) => u.email === userDetails.email)
                  if (updated) {
                    setUserDetails(updated)
                    setCurrentUser({
                      ...currentUser,
                      ...updated,
                    } as any)
                    sessionService.setUser({
                      ...currentUser,
                      ...updated,
                    })
                  }

                  // Show success dialog (same as Dashboard)
                  setUpdateSuccessOpen(true)
                  setIsEditing(false)
                } catch (err) {
                  console.error("Update failed:", err)
                  alert("Failed to update user.")
                } finally {
                  setIsUpdating(false)
                }
              }}
            />
          </div>
        </div>
      )}

      {/* Change Password Section */}
      <div className="w-full mt-8">
        <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-4">
          Change Password
        </h3>

        <div className="space-y-4 max-w-md">
          {/* Current Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Password
            </label>
            <input
              type="password"
              autoComplete="new-password"
              value={oldPassword ?? ""}
              onChange={(e) => setOldPassword(e.target.value)}
              placeholder="Enter current password"
              className="w-full border rounded-md p-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>

          {/* New Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value)
                if (confirmPassword && e.target.value !== confirmPassword) {
                  setPasswordError("Passwords do not match.")
                } else {
                  setPasswordError("")
                }
              }}
              placeholder="Enter new password"
              className="w-full border rounded-md p-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value)
                if (newPassword && e.target.value !== newPassword) {
                  setPasswordError("Passwords do not match.")
                } else {
                  setPasswordError("")
                }
              }}
              placeholder="Re-enter new password"
              className="w-full border rounded-md p-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            />
            {passwordError && (
              <p className="text-red-500 text-xs mt-1">{passwordError}</p>
            )}
          </div>

          {/* Submit Button */}
          <div className="pt-2">
            <button
              onClick={handlePasswordChange}
              disabled={isUpdating}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors duration-200"
            >
              {isUpdating ? "Updating..." : "Update Password"}
            </button>
          </div>
        </div>
      </div>

      <Dialog
        open={updateSuccessOpen}
        onClose={() => setUpdateSuccessOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              backgroundColor: "#4CAF50",
              margin: "0 auto 16px auto"
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-8 h-8"
              fill="none"
              viewBox="0 0 24 24"
              stroke="white"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            User Details Updated Successfully.
          </Typography>
        </DialogContent>
      </Dialog>

      <Dialog
        open={passwordSuccessOpen}
        onClose={() => setPasswordSuccessOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              backgroundColor: "#4CAF50",
              margin: "0 auto 16px auto"
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-8 h-8"
              fill="none"
              viewBox="0 0 24 24"
              stroke="white"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Password Updated Successfully.
          </Typography>
        </DialogContent>
      </Dialog>

    </div>
  )
}

export default MyAccount